import retry from 'promise-retry';
import { createModelMesh } from '../../modeling/geometry.js';
import {
  SHOP_PREVIEW_SCENE_DEFAULTS,
  applyCameraDefaults,
  applyOrbitControlsDefaults,
  applyRendererDefaults,
  addSceneLights,
  createMeshSpotLight,
} from '../../three/sceneConfig.js';

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";


THREE.CubeTextureLoader.prototype.crossOrigin = "Anonymous";
THREE.TextureLoader.prototype.crossOrigin = "Anonymous";

const hexColors = {
  brass: 0xFFDF68,
  silver: 0xf7f7f7,
  steel   : 0x2a2a2a
};

const ROTATION_SPEED = 0.24; // radians per second

// call this from within the react use effect / dom ready
export const ThreeScene = (mount, controls, pieceType, meshKey, color, genericError, windowWidth, threeParams, admin)=>{


  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////

  //        base values
  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////


  const urls = formatBoxUrls( threeParams.box_paths );

  const meshs = {};

  let width = mount.current.clientWidth
  let height = mount.current.clientHeight
  let frameId

  let reflectionCube;
  const sceneDefaults = SHOP_PREVIEW_SCENE_DEFAULTS;

  const scene = new THREE.Scene();
  const renderer = new THREE.WebGLRenderer({antialias:true,alpha: true });
  applyRendererDefaults(THREE, renderer);
  const camera = new THREE.OrthographicCamera( width/-2, width/2, height/2, height/-2, 0, 400 )
  const clock = new THREE.Clock(false);


  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////
  /////           define some functions
  //////////////////////////////////////////////////////////////////////////////


  const animate = ()=>{

    //requestAnimationFrame( animate );
    frameId = requestAnimationFrame(animate)
    if( orbitControls ){
      // required if controls.enableDamping = true, or if controls.autoRotate = true
      orbitControls.update();

    }
    render();

  }

  const render = ()=>{
    const delta = clock.getDelta();
    // comment this for now while we fix flickering
    // if( meshs != undefined && meshKey != undefined && meshs[meshKey] != undefined && meshs[meshKey] && admin !== true  ){
    //   meshs[meshKey].rotation.y -= ROTATION_SPEED * delta;
    // }
    ////if ( mesh ) mesh.rotation.x -= 0.006;

    renderer.render( scene, camera );
  }

  const handleResize = (event) => {

    if(
      mount &&
      mount.current &&
      mount.current.clientWidth &&
      mount.current.clientHeight

    ){

      width = mount.current.clientWidth
      height = mount.current.clientHeight

      camera.aspect = width / height

      var viewSize = height;
      var aspectRatio = width / height;

      const viewPort = {
          viewSize: viewSize,
          aspectRatio: aspectRatio,
          left: (-aspectRatio * viewSize) / 2,
          right: (aspectRatio * viewSize) / 2,
          top: viewSize / 2,
          bottom: -viewSize / 2,
      }

      camera.left = viewPort.left;
      camera.right = viewPort.right;
      camera.top = viewPort.top;
      camera.bottom = viewPort.bottom;

      camera.updateProjectionMatrix()

      if( windowWidth > 768 || windowWidth === width ){
        orbitControls.enabled = true;
      }else{
        orbitControls.enabled = false;
      }

      console.log('[ThreeScene handleResize]', {
        windowWidth,
        mountWidth: width,
        mountHeight: height,
        orbitEnabled: orbitControls.enabled,
        reason: windowWidth > 768 ? 'windowWidth > 768' : windowWidth === width ? 'windowWidth === mountWidth' : 'disabled (neither condition met)',
      });

      renderer.setSize( width, height);
      render()
    }

  }

  const start = () => {
    if (!frameId) {
      clock.start();
      frameId = requestAnimationFrame(animate)
    }
  }

  const stop = () => {
    cancelAnimationFrame(frameId)
    frameId = null
    clock.stop();
  }

  // this is only for admin - don't cache any
  // of the built meshs in the meshs object
  // delete them all every time
  const rerenderMesh = (newCurrentMesh, color, threeParams)=>{

    // make sure we can replace something
    if( Object.keys(meshs).length === 0 ) return;

    Object.entries(meshs).forEach(([meshKey, mesh]) => {
      mesh.visible = false;
      scene.remove( mesh );
      delete meshs[meshKey];
    });

    createMesh(newCurrentMesh, color, threeParams);
  };

  const replaceMesh = (newCurrentMesh, color, threeParams)=>{

    if( meshs[meshKey] !== undefined ){

      meshs[meshKey].visible = false;
      meshKey = newCurrentMesh;

      if( meshs[meshKey] == undefined ){
        createMesh(meshKey, color, threeParams);
      }else{
        meshs[meshKey].material.color.setHex( hexColors[color] );
        meshs[meshKey].visible = true;
      }
    }
  }

  const createMesh = (meshKey, color, threeParams )=>{

    let colorVal = hexColors[color];
    const settings = threeParams.settings[pieceType][meshKey];
    const aspects = threeParams[settings.id];

    const mesh = createModelMesh( {THREE, reflectionCube, meshKey, colorVal, aspects, ...settings} );

    scene.add( mesh );
    meshs[meshKey] = mesh;
  }

  const setColor = (newColor)=>{
    meshs[meshKey].material.color.setHex( hexColors[newColor] );
    color = newColor;
  }

  // texture loads with promises

  const getCubeTexture = (urls)=>{

    // load the box images and stuff next
    return retry(()=> textureLoad( new THREE.CubeTextureLoader(), urls) ).then((cube)=>{

      return cube;

      // create the thing on the screen
    }).catch(genericError);
  };

  const getMaterialTextures = (urls)=>{
    return urls.map( (url, i)=>{
      return retry(()=> textureLoad( new THREE.TextureLoader(), url) ).then((currentMap) => {

          const currentMat = new THREE.MeshBasicMaterial({
            map : currentMap,
            side: THREE.BackSide
          });

        return currentMat;

      }).catch(genericError);

    });
  };

  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////////////////////

  //                make some initialize settings
  //////////////////////////////////////////////////////////////////////////////


  renderer.setPixelRatio( window.devicePixelRatio );
  renderer.setSize( width, height);
  //renderer.setClearColor ('#d9d9d9', 1);
  //renderer.shadowMapEnabled = true;
  mount.current.appendChild(renderer.domElement)

  //camera = new THREE.PerspectiveCamera( 90, tw/th, 1, 1000 );
  applyCameraDefaults(camera);

  const orbitControls = new OrbitControls( camera, renderer.domElement );
  applyOrbitControlsDefaults(orbitControls, sceneDefaults.controls);
  addSceneLights(THREE, scene, sceneDefaults.lights);
  //spotLight.distance = 47;
  //spotLight.shadow.camera.near = 6;
  //spotLight.shadow.camera.far = 13;

   // admin debug
  //scene.add( new THREE.SpotLightHelper( spotLight, 5 ) );

  //var helper = new THREE.GridHelper( 1000, 4, 0xffffff, 0xffffff );
  //scene.add( helper );
  //scene.add( new THREE.BoxHelper( mesh ) );
  //var axisHelper = new THREE.AxisHelper( 5 );
  //scene.add( axisHelper );

  if( windowWidth < 768 ){
    orbitControls.enabled = false;
  }

  console.log('[ThreeScene init]', {
    windowWidth,
    mountWidth: width,
    mountHeight: height,
    orbitEnabled: orbitControls.enabled,
  });


  ///////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////

  //                  begin initialize the scene
  ///////////////////////////////////////////////////////////////

  // get the mesh material textures
  const materialTexturesPromises = getMaterialTextures( urls );

  // load the box images and stuff next
  const cubeTexturePromise = getCubeTexture(urls);

  // all the textures and the starting mesh is ready
  if (controls.current) {
    controls.current.sceneReady = false;
  }

  Promise.all([...materialTexturesPromises, cubeTexturePromise]).then((values)=>{

    reflectionCube = values.pop();
    scene.background = reflectionCube;
    reflectionCube.mapping = THREE.CubeRefractionMapping;


    //do the skybox
    const skyGeometry = new THREE.BoxGeometry( 100, 100, 100 );

    //var skyMaterial = new THREE.MeshFaceMaterial( skyGeometry, values );
    const skyBox = new THREE.Mesh( skyGeometry, values );
    //skyBox.receiveShadow = true;

    skyBox.position.setY(sceneDefaults.skybox.y);
    scene.add( skyBox );

    ///////////////////////////////////////////////////////////////
    ///////////////////////////////////////////////////////////////
    // set the default mesh to appear in the scene

    let colorVal = hexColors[color];
    const settings = threeParams.settings[pieceType][meshKey];
    const aspects = threeParams[settings.id];
    let size = meshKey;

    meshs[meshKey] = createModelMesh( {THREE, reflectionCube, size, colorVal, aspects, ...settings} );
    scene.add( meshs[meshKey] );
    camera.lookAt( meshs[meshKey] );
    orbitControls.target.copy( meshs[meshKey].position );
    controls.current.sceneReady = true;

    ///////////////////////////////////////////////////////////////
    ///////////////////////////////////////////////////////////////
    // add a spotlight and point it at the thing

    const spotLight2 = createMeshSpotLight(
      THREE,
      sceneDefaults.lights.meshSpot,
      meshs[meshKey].position
    );
    scene.add( spotLight2 );

    ///////////////////////////////////////////////////////////////
    ///////////////////////////////////////////////////////////////
    ///////////////////////////////////////////////////////////////
    // start

    //window.addEventListener('resize', handleResize)
    start()

  }).catch(genericError);

  // set the global stuff we want to get a hold of in component

  controls.current = { start, stop, replaceMesh, rerenderMesh, setColor, meshs, handleResize }

  return {
    stop,
    handleResize,
    renderer
  }
}

// promise based texture loading
const textureLoad = (loader,url)=>{
  return new Promise((resolve, reject)=>{

    loader.crossOrigin = "Anonymous";

    loader.load( url, (texture)=>{
        resolve(texture)

      },
      null,
      (error)=>{
        reject(error)
      });
  });

};

/*
Extra small   <576px
Small         ≥576px
Medium        ≥768px
Large         ≥992px
Extra large   ≥1200px
*/

const setZoom = ( width )=>{
  if( width < 576 ){
    return 2;
  }else if( width >= 576 ){
    return 4;
  }else if( width >= 768 ){
    return 5;
  }else if( width >= 992 ){
    return 6;
  }else{
    return 8;
  }
}


// construct these from props
// order is important bc we will map
/*
const urls = [
  box3_path, box3_path,
  box3_path, box1_path,
  box3_path, box3_path
];

// construct these from props
const purls = [
  box2_path, box2_path,
  box2_path, box1_path,
  box2_path, box2_path
];
*/


const formatBoxUrls = ([box1_path, box2_path, box3_path])=>{
  return [
    box3_path, box3_path,
    box3_path, box1_path,
    box3_path, box3_path
  ];
}

/*
function withTimeout(promise, timeout){
    const timeoutPromise = new Promise((resolve, reject) =>
        setTimeout(
          () => {
            return reject(`Timed out after ${timeout} ms.`)
          },
            timeout));
    return Promise.race([
        promise,
        timeoutPromise
    ]);
};

// promise retry
// pass an arrow function in - it doesnt handle params
function retry(fn, retriesLeft = 5, interval = 500, initialTimeout = 5000) {

  return new Promise((resolve, reject) => {

    withTimeout(fn(), initialTimeout)
      .then(resolve)
      .catch((error) => {
        console.log("caught errors!");
        console.log( error );

        if (retriesLeft === 0) {
          console.log("rejecting!");
          reject('maximum retries exceeded', error);
          return;
        }

        console.log("ab out to set error timeoput");

        setTimeout(() => {

          console.log(`timed out. retries left: ${retriesLeft} : interval: ${interval}`);
          // Passing on "reject" is the important part
          retry(fn, retriesLeft - 1, interval + 500).then(resolve, reject);
        }, interval);
    });
  });
}
*/
